import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PencilSquareIcon, MapPinIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import EscalationLevelModal from '../components/EscalationLevelModal';

const GrievanceLevel3Page = () => {
    const [level, setLevel] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/admin/grievance-escalation/levels');
            const level3 = (response.data.data || []).find(l => l.level_number === 3);
            setLevel(level3);
        } catch (error) {
            console.error('Error fetching level 3:', error);
            toast.error('Failed to load Level 3 details');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleSubmit = async (formData) => {
        try {
            setSubmitting(true);
            await axios.put(`/api/admin/grievance-escalation/levels/${level.id}`, formData);
            toast.success('Level 3 details updated successfully');
            setIsModalOpen(false);
            fetchData();
        } catch (error) {
            console.error('Error updating level 3:', error);
            toast.error('Failed to update Level 3 details');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return <div className="bg-white rounded-3xl h-64 animate-pulse border border-slate-100 shadow-sm"></div>;
    }

    if (!level) {
        return <div className="text-center p-8 bg-white rounded-3xl shadow-sm border border-slate-100">Level 3 data not found.</div>;
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-slate-800">Level 3 Escalation</h1>
                    <p className="text-slate-500 mt-1">Manage contact details for National Housing Bank (NHB)</p>
                </div>
                <button
                    onClick={() => setIsModalOpen(true)}
                    className="flex items-center gap-2 text-amber-600 hover:bg-amber-50 px-6 py-3 rounded-xl transition-colors font-semibold border border-amber-100 shadow-sm"
                >
                    <PencilSquareIcon className="h-5 w-5" />
                    Edit Level 3
                </button>
            </div>

            <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
                <div className="h-2 bg-amber-500"></div>
                <div className="p-8">
                    <div className="flex items-start justify-between mb-8">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-2xl flex items-center justify-center font-bold text-2xl bg-amber-50 text-amber-600">
                                {level.level_number}
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-slate-800">{level.section_title}</h2>
                                <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mt-1 ${level.is_active ? 'bg-green-50 text-green-600' : 'bg-slate-100 text-slate-500'
                                    }`}>
                                    {level.is_active ? 'Active' : 'Inactive'}
                                </span>
                            </div>
                        </div>
                    </div>

                    <p className="text-slate-600 mb-10 max-w-4xl text-lg leading-relaxed">
                        {level.section_description}
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                        <div className="space-y-4">
                            <div className="flex items-center gap-3 text-slate-400">
                                <MapPinIcon className="h-6 w-6" />
                                <span className="text-sm font-bold uppercase tracking-wider">Postal Address</span>
                            </div>
                            <div className="bg-slate-50 p-8 rounded-3xl text-slate-700 whitespace-pre-line leading-relaxed font-semibold border border-slate-100 text-lg shadow-inner">
                                {level.postal_address}
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div className="flex items-center gap-3 text-slate-400">
                                <GlobeAltIcon className="h-6 w-6" />
                                <span className="text-sm font-bold uppercase tracking-wider">Online Redressal Portal</span>
                            </div>
                            <div className="p-8 bg-slate-50 rounded-3xl border border-slate-100">
                                <a
                                    href={level.online_portal_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-primary-600 font-bold hover:underline flex items-center gap-2 text-xl break-all"
                                >
                                    {level.online_portal_url}
                                </a>
                                <p className="text-slate-500 mt-4 text-sm font-medium">NHB Online Grievance Redressal System (GRIDS)</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <EscalationLevelModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSubmit={handleSubmit}
                level={level}
                loading={submitting}
            />
        </div>
    );
};

export default GrievanceLevel3Page;
